import React, { useMemo } from 'react';

/**
 * MarkdownContent - A lightweight markdown renderer for the Outlook add-in
 * Supports basic markdown formatting and citation bubbles
 */
export function MarkdownContent({ content, onCitationClick, sources = [] }) {
  // Build citation mapping for sequential numbering
  const citationMapping = useMemo(() => {
    const mapping = new Map();
    let nextNumber = 1;
    
    const regex = /(@@SOURCE_CITATION_(\d+)@@|\[\[([^\]]+)\]\])/g;
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      const citationId = match[2] ? `SOURCE_${match[2]}` : match[3] || '';
      
      if (citationId && !mapping.has(citationId)) {
        mapping.set(citationId, nextNumber++);
      }
    }
    
    return mapping;
  }, [content]);

  // Process content and replace citations with bubbles
  const processedContent = useMemo(() => {
    if (!content) return [];
    
    const parts = [];
    const regex = /(@@SOURCE_CITATION_(\d+)@@|\[\[([^\]]+)\]\])/g;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }
      
      // Add citation bubble
      const citationId = match[2] ? `SOURCE_${match[2]}` : match[3] || '';
      const displayNumber = citationMapping.get(citationId) || 1;
      
      parts.push({
        type: 'citation',
        number: displayNumber,
        citationId: citationId
      });
      
      lastIndex = regex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }
    
    return parts;
  }, [content, citationMapping]);

  // Format text with basic markdown
  const formatText = (text) => {
    // Split by newlines and process each line
    const lines = text.split('\n');
    const elements = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Handle headers
      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={`line-${i}`} className="font-semibold text-base mt-3 mb-2">
            {formatInline(line.substring(4))}
          </h3>
        );
        continue;
      }
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`line-${i}`} className="font-semibold text-lg mt-3 mb-2">
            {formatInline(line.substring(3))}
          </h2>
        );
        continue;
      }
      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={`line-${i}`} className="font-semibold text-xl mt-3 mb-2">
            {formatInline(line.substring(2))}
          </h1>
        );
        continue;
      }
      
      // Handle bullet points
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        elements.push(
          <div key={`line-${i}`} className="flex gap-2 ml-4 my-1">
            <span className="text-blue-600">•</span>
            <span>{formatInline(line.trim().substring(2))}</span>
          </div>
        );
        continue;
      }
      
      // Handle bold text with **
      if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
        elements.push(
          <p key={`line-${i}`} className="font-bold my-1">
            {formatInline(line.trim().slice(2, -2))}
          </p>
        );
        continue;
      }
      
      // Regular paragraph
      if (line.trim()) {
        elements.push(
          <p key={`line-${i}`} className="my-1">
            {formatInline(line)}
          </p>
        );
      } else {
        // Empty line creates space
        elements.push(<br key={`line-${i}`} />);
      }
    }
    
    return elements;
  };

  // Format inline markdown (bold, italic, code)
  const formatInline = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;
    
    while (remaining.length > 0) {
      // Bold with **
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      if (boldMatch) {
        const before = remaining.substring(0, boldMatch.index);
        if (before) parts.push(before);
        parts.push(<strong key={`bold-${key++}`} className="font-bold">{boldMatch[1]}</strong>);
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
        continue;
      }
      
      // Italic with *
      const italicMatch = remaining.match(/\*([^*]+)\*/);
      if (italicMatch) {
        const before = remaining.substring(0, italicMatch.index);
        if (before) parts.push(before);
        parts.push(<em key={`italic-${key++}`} className="italic">{italicMatch[1]}</em>);
        remaining = remaining.substring(italicMatch.index + italicMatch[0].length);
        continue;
      }
      
      // Inline code with `
      const codeMatch = remaining.match(/`([^`]+)`/);
      if (codeMatch) {
        const before = remaining.substring(0, codeMatch.index);
        if (before) parts.push(before);
        parts.push(
          <code key={`code-${key++}`} className="bg-gray-200 px-1 rounded text-xs font-mono">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.substring(codeMatch.index + codeMatch[0].length);
        continue;
      }
      
      // No more special formatting
      parts.push(remaining);
      break;
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Combine parts into lines to maintain inline flow
  const renderContent = () => {
    const lines = [];
    let currentLine = [];
    
    processedContent.forEach((part, index) => {
      if (part.type === 'text') {
        // Split text by newlines
        const textLines = part.content.split('\n');
        textLines.forEach((line, lineIndex) => {
          if (lineIndex > 0) {
            // New line - push current line and start new one
            lines.push(currentLine);
            currentLine = [];
          }
          if (line) {
            currentLine.push({ type: 'text', content: line, key: `text-${index}-${lineIndex}` });
          }
        });
      } else if (part.type === 'citation') {
        // Add citation to current line
        currentLine.push({ 
          type: 'citation', 
          number: part.number, 
          citationId: part.citationId,
          key: `cite-${index}` 
        });
      }
    });
    
    // Push the last line
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }
    
    // Render lines
    return lines.map((line, lineIndex) => {
      const firstTextIndex = line.findIndex((item) => item.type === 'text');
      const firstText = firstTextIndex >= 0 ? line[firstTextIndex] : null;
      const lineText = firstText?.content || '';
      const headerMatch = lineText.match(/^(#{1,3})\s+/);
      const bulletMatch = !headerMatch && lineText.trim().match(/^[-*]\s+/);
      const stripPattern = headerMatch
        ? new RegExp(`^${headerMatch[1]}\\s+`)
        : bulletMatch
          ? /^\s*[-*]\s+/
          : null;

      const lineElements = line.map((item, idx) => {
        if (item.type === 'text') {
          let text = item.content;
          if (idx === firstTextIndex && stripPattern) {
            text = text.replace(stripPattern, '');
          }
          return <span key={item.key}>{formatInline(text)}</span>;
        }
        if (item.type === 'citation') {
          return (
            <span
              key={item.key}
              className={`inline-flex items-center justify-center h-5 w-5 bg-blue-600 text-white text-xs font-semibold rounded-full align-baseline mx-0.5 ${
                onCitationClick ? 'cursor-pointer hover:opacity-80' : ''
              }`}
              title={`Source ${item.number}`}
              onClick={
                onCitationClick
                  ? (event) => onCitationClick(item.number, event, item.citationId)
                  : undefined
              }
            >
              {item.number}
            </span>
          );
        }
        return null;
      });

      if (headerMatch) {
        const level = Math.min(headerMatch[1].length, 3);
        const headingClass =
          level === 1
            ? 'font-semibold text-xl mt-3 mb-2'
            : level === 2
              ? 'font-semibold text-lg mt-3 mb-2'
              : 'font-semibold text-base mt-3 mb-2';
        const HeadingTag = `h${level}`;
        return (
          <HeadingTag key={`line-${lineIndex}`} className={headingClass}>
            {lineElements}
          </HeadingTag>
        );
      }

      if (bulletMatch) {
        return (
          <div key={`line-${lineIndex}`} className="flex gap-2 ml-4 my-1">
            <span className="text-blue-600">•</span>
            <span>{lineElements}</span>
          </div>
        );
      }

      return (
        <p key={`line-${lineIndex}`} className="my-1">
          {lineElements}
        </p>
      );
    });
  };

  return (
    <div className="break-words">
      {renderContent()}
    </div>
  );
}

